<script setup lang="ts">
   import { computed, onMounted, ref, watch } from 'vue';
   // @ts-ignore
   import MmenuLight from 'mmenu-light';
   import 'mmenu-light/dist/mmenu-light.css';
   import LmsNavListItem from '~/components/LmsNavListItem.vue';
   import { useUserProfileMenu } from '~/composables/useUserProfileMenu';
   import NuxtTypedLink from '~/components/NuxtTypedLink.vue';
   import { useLmsNavigation } from '~/composables/useLmsNavigation';

   const route = useRoute();

   const { profileNavigationItems, showProfileFeatures, adminMenuItem } = useUserProfileMenu();
   const { settingsLoaded, visibleNavItems, getNavRoute, isExternalLink } = useLmsNavigation();

   const menuElementRef = ref(null);
   let drawerInstance: any = null;

   type MenuItem = {
      label: string;
      to?: any;
      items?: MenuItem[];
      isExternal?: boolean;
   };

   const items = computed<MenuItem[]>(() => {
      return visibleNavItems.value.map(item => {
         const route = getNavRoute(item.key);
         const children = item.children?.length
            ? item.children.map(child => ({
               label: child.label,
               to: getNavRoute(child.key),
               isExternal: isExternalLink(child.key),
            }))
            : undefined;

         if (children?.length) {
            return { label: item.label, items: children };
         }

         return {
            label: item.label,
            to: route,
            isExternal: isExternalLink(item.key),
         };
      });
   });

   onMounted(() => {
      if (menuElementRef.value) {
         const menu = new MmenuLight(
            menuElementRef.value,
            '(max-width: 1023px)',
         );

         menu.navigation({});
         drawerInstance = menu.offcanvas({});
      }
   });

   function openDrawer() {
      if (drawerInstance) {
         drawerInstance.open();
      }
   }

   defineExpose({
      openMenu: openDrawer,
   });

   watch(
      () => route.fullPath,
      () => {
         if (drawerInstance) {
            drawerInstance.close();
         }
      },
   );
</script>

<template>
   <nav ref="menuElementRef" class="mx-auto" id="app-main-menu">
      <ul class="flex">
         <template v-for="item in items" :key="item.label">
            <!-- Dropdown parent with children -->
            <li
               v-if="item.items && item.items.length > 0"
               class="group relative flex">
               <span
                  class="flex cursor-pointer items-center justify-between gap-1 whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                  {{ item.label }}
                  <svg
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24"
                     fill="currentColor"
                     class="hidden size-4 lg:block">
                     <path
                        fill-rule="evenodd"
                        d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                        clip-rule="evenodd" />
                  </svg>
               </span>
               <ul
                  class="lg:invisible lg:absolute lg:left-0 lg:top-full lg:z-50 lg:min-w-48 lg:border lg:border-gray-200 lg:bg-white lg:opacity-0 lg:shadow-lg lg:transition-all lg:duration-200 lg:group-hover:visible lg:group-hover:opacity-100">
                  <template v-for="subItem in item.items" :key="subItem.label">
                     <li v-if="subItem.to" class="flex">
                        <a
                           v-if="subItem.isExternal"
                           :href="subItem.to"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </a>
                        <NuxtTypedLink
                           v-else
                           :to="subItem.to"
                           class="flex w-full items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                           {{ subItem.label }}
                        </NuxtTypedLink>
                     </li>
                  </template>
               </ul>
            </li>

            <!-- External link (e.g. members_portal) -->
            <li v-else-if="item.isExternal && item.to" class="flex">
               <a
                  :href="item.to"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                  {{ item.label }}
               </a>
            </li>

            <!-- Regular internal link -->
            <LmsNavListItem v-else-if="item.to" :to="item.to">
               {{ item.label }}
            </LmsNavListItem>
         </template>

         <!-- Mobile profile submenu -->
         <li
            v-if="settingsLoaded && showProfileFeatures"
            class="sm:hidden">
            <span
               class="flex items-center justify-between border-b-[3px] border-transparent bg-transparent p-3 text-sm font-semibold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
               Profile
            </span>
            <ul>
               <LmsNavListItem
                  :to="profileSubmenuItem.to"
                  v-for="profileSubmenuItem in profileNavigationItems"
                  :key="profileSubmenuItem.label">
                  {{ profileSubmenuItem.label }}
               </LmsNavListItem>
               <li v-if="adminMenuItem">
                  <a
                     :href="adminMenuItem.url"
                     target="_blank"
                     rel="noopener noreferrer"
                     class="flex items-center justify-between whitespace-nowrap border-b-[3px] border-transparent bg-transparent p-3 text-xs font-bold uppercase text-gray-400 hover:border-brand-primary hover:text-black">
                     Admin
                  </a>
               </li>
            </ul>
         </li>
      </ul>
   </nav>
</template>

<style scoped></style>
